<template>
  <div class="root-container">
    <BIcon
      class="back-icon"
      outlined
      size="large"
      icon-weight="light"
      :disabled="!canGoBack"
      @click="backToLead"
    >
      arrow_circle_left
    </BIcon>
    <div class="lead-info">
      <div class="company-name">
        {{ callTarget?.companyInfo?.companyName }}
      </div>
      <div
        v-if="callTarget?.callTargetInfo?.name"
        class="call-target-name"
      >
        {{ callTarget?.callTargetInfo?.name }}
      </div>
    </div>
    <div class="call-info">
      <BIcon
        type="gray"
      >
        call
      </BIcon>
      <div>
        {{ callResultTime }}
      </div>
    </div>
    <div
      v-if="isAvailableCallFeedbackFeature && authorize(['owner', 'member'])"
      class="call-fb-link"
    >
      <CallFeedbackLink
        :call-result-id="callResult?.id"
        :start-date="callFeedbackStartDate"
        :end-date="callFeedbackEndDate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import { CallResult, CallTarget } from '@/api/openapi';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import CallFeedbackLink from '@/components/organisms/user/general/CallFeedbackLink.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useWait } from '@/composable/vue-wait';
import { key } from '@/store';
import { startOf, add, formatShorterDate, formatShortTime, formatISO8601Date } from '@/utils/date-time';

type TProps = {
  callResult: CallResult | null;
  backToLead: () => void;
  canGoBack: boolean;
}
type TEmits = {
  'loading': [boolean];
}

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

const { doActionWithWait, wait } = useWait();

const { inject: injectCurrentUser } = useCurrentUserProvider();
const { authorize, currentUser } = injectCurrentUser();

// useAvailableFeatureCheck が Vuex user.client に依存しているので読み込む
const store = useStore(key);
const fetchClient = async () => {
  await doActionWithWait('fetchClientWait', async () => {
    await store.dispatch('user/getClientAction', { id: currentUser.value?.clientId });
  });
};
onBeforeMount(async () => {
  await fetchClient();
});

const {
  isAvailableCallFeedbackFeature,
} = useAvailableFeatureCheck();

const callTarget = ref<CallTarget>();
const callTargetId = computed(() => props.callResult?.callTargetId);

const callResultTime = computed(() => {
  const startedAtDate = formatShorterDate(props.callResult?.startedAt);
  const startedAtTime = formatShortTime(props.callResult?.startedAt);
  const endedAtTime = formatShortTime(props.callResult?.endedAt);
  return `${startedAtDate} ${startedAtTime} - ${endedAtTime}`;
});

const callFeedbackStartDate = computed(() => {
  return formatISO8601Date(startOf(props.callResult?.startedAt, 'day'));
});

const callFeedbackEndDate = computed(() => {
  return formatISO8601Date(add(startOf(props.callResult?.startedAt, 'day'), 14, 'day'));
});

const loading = computed(() => {
  return wait.is('fetchCallTargetWait');
});

const fetchCallTarget = async () => {
  doActionWithWait('fetchCallTargetWait', async () => {
    const api = new CallTargetApiService();
    if (!callTargetId.value) return;
    const { data: { callTarget: callTargetRes } } = await api.getCallTarget({ request: { callTargetId: callTargetId.value } });
    callTarget.value = callTargetRes;
  });
};

watch(callTargetId, fetchCallTarget);
watch(loading, (newValue) => {
  emit('loading', newValue);
});
</script>

<style lang="scss" scoped>
.root-container {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  padding: 20px 32px;
}

.lead-info {
  display: flex;
  gap: 13px;
}

.call-target-name {
  position: relative;
}

.call-target-name::before {
	position: absolute;
	top: calc(50% - 7px);
	left: -7px;
	content: "";
	width: 1px;
	height: 14px;
	background-color: $textcolor-base;
}


.call-info {
  display: flex;
  align-items: center;
  gap: 4px;

  color: $textcolor-light;
}

.back-icon {
  cursor: pointer;
}

.call-fb-link {
  margin-left: auto;

  :deep(.b-icon) {
    padding-top: 2px;
  }
}
</style>
